import Vue from 'vue';
import _ from 'lodash';

import {
  getGroups,
  addGroup,
  updateGroup,
  deleteGroup,
} from '@/services/planification';

const getNonCircularGroupProperties = group => {
  return {
    alias: group.alias,
    end_datetime: group.end_datetime,
    start_datetime: group.start_datetime,
    parent_group: group.parent_group,
    username: group.username,
    name: group.name,
  };
};

export const state = {
  index: {},
  sort: {
    groupName: null,
  },
};

export const mutations = {
  setGroups(state, groups) {
    groups.forEach(group => {
      Vue.set(state.index, group.username, group);
    });
  },

  setGroup(state, group) {
    Vue.set(state.index, group.username, group);
  },

  deleteGroup(state, group) {
    Vue.delete(state.index, group.username);
  },

  addGroupMember(state, { group, member }) {
    state.index[group.username].members.push(member);
  },

  removeGroupMember(state, { group, member }) {
    state.index[group.username].members = state.index[
      group.username
    ].members.filter(localMember => localMember.username !== member.username);
  },

  addGroupMembership(state, { group, membership }) {
    state.index[group.username].memberships.push(membership);
  },

  removeGroupMembership(state, { group, membership }) {
    state.index[group.username].memberships = state.index[
      group.username
    ].memberships.filter(
      localMembership => localMembership.username !== membership.username
    );
  },

  setSortGroupName(state, groupName) {
    state.sort.groupName = groupName;
  },
};

export const actions = {
  getGroups({ commit }) {
    return getGroups().then(response => {
      commit('setGroups', response.data);
      return response;
    });
  },

  addGroup({ commit, getters, dispatch }, group) {
    return addGroup(group).then(response => {
      const localGroup = response.data;
      const parentGroup = getters.getCopyGroupByUsername(
        group.parent_group?.username
      );

      if (parentGroup) {
        // Add the parent group to the members of the newly created group
        localGroup.memberships.push(parentGroup);
        commit('setGroup', localGroup);

        // Add the newly created group to the parent group memberships
        return dispatch('updateGroup', {
          ...parentGroup,
          members: [...parentGroup.members, localGroup],
        });
      } else {
        commit('setGroup', localGroup);
      }

      return response;
    });
  },

  updateGroup({ commit }, group) {
    return updateGroup(group).then(response => {
      commit('setGroup', response.data);
      return response;
    });
  },

  deleteGroupRequest({ commit }, group) {
    return deleteGroup(group).then(response => {
      commit('deleteGroup', group);
      return response;
    });
  },

  deleteGroup({ state, getters, dispatch }, group) {
    const promises = [];

    // Also delete subgroups if the group is their only parent.
    const subgroups = group.members.filter(member =>
      member.user ? undefined : state.index[member.username]
    );
    subgroups.forEach(subgroup => {
      const localSubGroup = state.index[subgroup.username];
      if (localSubGroup.memberships?.length <= 1) {
        promises.push(dispatch('deleteGroupRequest', localSubGroup));
      }
    });

    const parentGroup = getters.getCopyGroupByUsername(
      group.parent_group?.username
    );
    // Update the parent if there's one
    if (parentGroup) {
      parentGroup.members = parentGroup.members.filter(
        member => member.username !== group.username
      );
      promises.push(dispatch('updateGroup', parentGroup));
    }
    // Permanently delete the group if there's no other parents
    if (group.memberships.length <= 1) {
      promises.push(dispatch('deleteGroupRequest', group));
    }

    return Promise.all(promises);
  },

  addGroupUser({ getters, dispatch }, user) {
    const group = getters.getCopyGroupByUsername(user.parent_group.username);
    group.members.push(user);

    return dispatch('updateGroup', group);
  },

  updateGroupUser({ getters, dispatch }, user) {
    const group = getters.getCopyGroupByUsername(user.parent_group.username);
    group.members = _.unionBy([user], group.members, 'id');

    return dispatch('updateGroup', group);
  },

  deleteGroupUser({ getters, dispatch }, user) {
    const group = getters.getCopyGroupByUsername(user.parent_group.username);
    group.members = group.members.filter(member => member.id !== user.id);

    return dispatch('updateGroup', group);
  },
};

export const getters = {
  getGroupByUsername: state => username => {
    return state.index[username];
  },

  getCopyGroupByUsername: state => username => {
    return username && JSON.parse(JSON.stringify(state.index[username]));
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
